import { Add as AddIcon, Lock as LockIcon, Public as PublicIcon } from '@mui/icons-material';
import { Box, Button, IconButton, Menu, MenuItem, TableRow, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Context } from '../../service/Context';
import { capitalizar, formatFecha, ordenarLista } from '../../utils/UtilesComun';
import { obtenerColorNota } from '../../utils/UtilesJugador';
import { BoxTitleApp, TableCellApp, TableModuleApp, VisibilidadIconApp } from '../custom/CustomComponents';
import ModalInformesJugador from '../jugador/ModalInformesJugador';
import ModalSeleccionarJugador from '../jugador/ModalSeleccionarJugador';

const columnsFull = [
  { field: 'timestamp', label: 'Fecha', width: '15%', tooltip: 'Fecha del informe', sx: { pl: 1, pr: 0.5 } },
  { field: 'nombreJugador', label: 'Jugador', width: '27%', tooltip: 'Nombre del jugador', sx: { pl: 1, pr: 0.5 } },
  {
    field: 'visibilidad',
    label: 'Vis.',
    width: '7%',
    align: 'center',
    sortable: false,
    tooltip: 'Visibilidad',
    sx: { px: 0.25 },
  },
  {
    field: 'notaTactico',
    label: 'Tác.',
    width: '8.5%',
    align: 'center',
    sortable: false,
    tooltip: 'Área Táctica',
    sx: { px: 0.25 },
  },
  {
    field: 'notaTecnico',
    label: 'Téc.',
    width: '8.5%',
    align: 'center',
    sortable: false,
    tooltip: 'Área Técnica',
    sx: { px: 0.25 },
  },
  {
    field: 'notaPsicologico',
    label: 'Psi.',
    width: '8.5%',
    align: 'center',
    sortable: false,
    tooltip: 'Área Psicológica',
    sx: { px: 0.25 },
  },
  {
    field: 'notaEntorno',
    label: 'Ent.',
    width: '8.5%',
    align: 'center',
    sortable: false,
    tooltip: 'Área Entorno',
    sx: { px: 0.25 },
  },
  {
    field: 'nota',
    label: 'Nota',
    width: '15%',
    align: 'center',
    sortable: false,
    tooltip: 'Calificación global (clic para ver informe)',
    sx: { px: 0.25, borderLeft: '1px solid #e2e8f0' },
  },
];

const columnsCompact = [
  { field: 'timestamp', label: 'Fecha', width: '22%', tooltip: 'Fecha del informe', sx: { pl: 1, pr: 0.5 } },
  { field: 'nombreJugador', label: 'Jugador', width: '54%', tooltip: 'Nombre del jugador', sx: { pl: 0.5, pr: 0.5 } },
  {
    field: 'nota',
    label: 'Nota',
    width: '16%',
    align: 'center',
    sortable: true,
    tooltip: 'Nota media / final del informe',
    sx: { px: 0.25 },
  },
  {
    field: 'visibilidad',
    label: 'Vis.',
    width: '8%',
    align: 'center',
    sortable: true,
    tooltip: 'Visibilidad',
    sx: { px: 0.25 },
  },
];

/**
 * Calcula la nota a mostrar para un informe: si tiene notaFinal, la devuelve;
 * si no, calcula la media de las áreas disponibles; si no hay ninguna, devuelve null.
 */
function calcularNotaInforme(inf) {
  if (inf.notaFinal !== null && inf.notaFinal !== undefined && inf.notaFinal !== '') {
    return Number(inf.notaFinal);
  }
  const notasAreas = [inf.notaTactico, inf.notaTecnico, inf.notaPsicologico, inf.notaEntorno]
    .filter((n) => n !== null && n !== undefined && n !== '')
    .map(Number);
  if (notasAreas.length > 0) {
    const suma = notasAreas.reduce((acc, curr) => acc + curr, 0);
    return Number((suma / notasAreas.length).toFixed(1));
  }
  return null;
}

const FilaInforme = ({ informe, columns, compact, onSelectInforme, onToggleVisibility }) => {
  const navigate = useNavigate();
  const [visibilidadAnchorEl, setVisibilidadAnchorEl] = useState(null);

  const handleVisibilityClick = (event) => {
    setVisibilidadAnchorEl(event.currentTarget);
  };

  const handleVisibilityClose = () => {
    setVisibilidadAnchorEl(null);
  };

  const handleVisibilityChange = (nueva) => {
    if (onToggleVisibility) {
      onToggleVisibility(informe, nueva === 'PUBLICA');
    }
    handleVisibilityClose();
  };

  const valorNota = calcularNotaInforme(informe);
  const estiloNota = obtenerColorNota(valorNota);
  const textoNota = valorNota !== null ? valorNota.toFixed(1) : '-';

  return (
    <TableRow hover>
      {columns.map((col) => {
        if (
          col.field === 'notaTactico' ||
          col.field === 'notaTecnico' ||
          col.field === 'notaPsicologico' ||
          col.field === 'notaEntorno'
        ) {
          const valorArea = informe[col.field];
          const tieneNota = valorArea !== null && valorArea !== undefined && valorArea !== '';
          const estiloArea = tieneNota ? obtenerColorNota(Number(valorArea)) : null;

          return (
            <TableCellApp
              key={col.field}
              align="center"
              sx={{
                width: col.width,
                py: 0.8,
                px: 0.25,
                textAlign: 'center',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                {tieneNota ? (
                  <Tooltip title={`${col.tooltip}: ${Number(valorArea).toFixed(1)}`} arrow>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 19,
                        minWidth: 24,
                        px: 0.4,
                        borderRadius: '3px',
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        backgroundColor: estiloArea.bg,
                        color: estiloArea.color,
                        border: `1px solid ${estiloArea.border}`,
                      }}
                    >
                      {Number(valorArea).toFixed(1)}
                    </Box>
                  </Tooltip>
                ) : (
                  <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.70rem' }}>
                    -
                  </Box>
                )}
              </Box>
            </TableCellApp>
          );
        }

        if (col.field === 'nota') {
          return (
            <TableCellApp
              key={col.field}
              align="center"
              sx={{
                width: col.width,
                py: 0.8,
                px: 0.25,
                textAlign: 'center',
                ...(!compact && { borderLeft: '1px solid #e2e8f0' }),
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                <Tooltip
                  title={valorNota !== null ? `Nota Final: ${textoNota} (clic para ver informe)` : 'Ver informe'}
                  arrow
                >
                  <Box
                    onClick={() => onSelectInforme(informe)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 25,
                      minWidth: 36,
                      px: 0.9,
                      borderRadius: '5px',
                      fontSize: '0.76rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.15s ease-in-out',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
                      backgroundColor: estiloNota.bg,
                      color: estiloNota.color,
                      border: `1px solid ${estiloNota.border}`,
                      '&:hover': {
                        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.14)',
                        transform: 'translateY(-0.5px)',
                        filter: 'brightness(0.97)',
                      },
                    }}
                  >
                    {textoNota}
                  </Box>
                </Tooltip>
              </Box>
            </TableCellApp>
          );
        }

        if (col.field === 'visibilidad') {
          return (
            <TableCellApp
              key={col.field}
              align="center"
              sx={{
                width: col.width,
                py: 0.8,
                px: 0.25,
                textAlign: 'center',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                <Tooltip title="Visibilidad: Clic para cambiar" arrow>
                  <IconButton
                    size="small"
                    onClick={handleVisibilityClick}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{
                      width: 22,
                      height: 22,
                      p: 0,
                      minWidth: 22,
                      margin: '0 auto',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: 'text.secondary',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        color: 'primary.main',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '0.78rem',
                        color: 'text.secondary',
                      },
                    }}
                  >
                    <VisibilidadIconApp
                      visibilidad={informe.publica ? 'PUBLICA' : 'PRIVADA'}
                      sx={{ fontSize: '0.78rem', color: 'text.secondary' }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
              <Menu anchorEl={visibilidadAnchorEl} open={Boolean(visibilidadAnchorEl)} onClose={handleVisibilityClose}>
                <MenuItem onClick={() => handleVisibilityChange('PRIVADA')} sx={{ fontSize: '0.8rem' }}>
                  <LockIcon sx={{ fontSize: '1rem', color: 'text.secondary', mr: 1 }} /> Privada
                </MenuItem>
                <MenuItem onClick={() => handleVisibilityChange('PUBLICA')} sx={{ fontSize: '0.8rem' }}>
                  <PublicIcon sx={{ fontSize: '1rem', color: 'text.secondary', mr: 1 }} /> Pública
                </MenuItem>
              </Menu>
            </TableCellApp>
          );
        }

        if (col.field === 'nombreJugador') {
          return (
            <TableCellApp
              key={col.field}
              sx={{
                width: col.width,
                py: 0.8,
                pl: compact ? 0.5 : 1,
                pr: 0.5,
                overflow: 'hidden',
              }}
            >
              <Tooltip title={capitalizar(informe.nombreJugador || 'Jugador')} arrow placement="top">
                <Box
                  onClick={() => navigate(`/jugador/${informe.idJugador}`)}
                  sx={{
                    cursor: 'pointer',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontWeight: 500,
                    fontSize: '0.80rem',
                    color: 'text.primary',
                    '&:hover': {
                      color: 'primary.main',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {capitalizar(informe.nombreJugador || 'Jugador')}
                </Box>
              </Tooltip>
            </TableCellApp>
          );
        }

        if (col.field === 'timestamp') {
          const { completa } = formatFecha(informe.timestamp);
          const fechaGuiones = completa ? completa.replace(/\//g, '-') : '-';
          return (
            <TableCellApp
              key={col.field}
              sx={{
                width: col.width,
                py: 0.8,
                pl: 1,
                pr: 0.5,
                textAlign: 'left',
                whiteSpace: 'nowrap',
              }}
            >
              <Tooltip title={fechaGuiones} arrow>
                <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.78rem', fontWeight: 500 }}>
                  {fechaGuiones}
                </Box>
              </Tooltip>
            </TableCellApp>
          );
        }

        return (
          <TableCellApp key={col.field} sx={{ width: col.width, py: 0.8 }}>
            {informe[col.field]}
          </TableCellApp>
        );
      })}
    </TableRow>
  );
};

FilaInforme.propTypes = {
  informe: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  compact: PropTypes.bool,
  onSelectInforme: PropTypes.func.isRequired,
  onToggleVisibility: PropTypes.func,
};

const InformesUsuario = ({
  informes = [],
  onUpdate,
  compact = false,
  showCreateButton = true,
  equiposClub = null,
  title = 'Mis Informes',
}) => {
  const { editarInforme, obtenerInformesJugador } = useContext(Context);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(compact ? 5 : 10);
  const [sort, setSort] = useState({ field: 'timestamp', direction: 'desc' });

  // Modal para ver/editar informe del jugador seleccionado
  const [modalOpen, setModalOpen] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [selectorJugadorOpen, setSelectorJugadorOpen] = useState(false);
  const [jugadorSeleccionado, setJugadorSeleccionado] = useState(null);
  const [informesJugadorModal, setInformesJugadorModal] = useState([]);

  const columns = compact ? columnsCompact : columnsFull;

  const handleSelectInforme = async (inf) => {
    setIsCreateMode(false);
    setJugadorSeleccionado({
      idJugador: inf.idJugador,
      nombre: inf.nombreJugador,
    });
    try {
      const data = await obtenerInformesJugador(inf.idJugador);
      setInformesJugadorModal(data && data.length > 0 ? data : [inf]);
    } catch {
      setInformesJugadorModal([inf]);
    }
    setModalOpen(true);
  };

  const handleSelectJugadorParaCrear = async (jugador) => {
    setSelectorJugadorOpen(false);
    const idJug = jugador.idJugador;
    const nomJug = jugador.nombreJugador || jugador.nombre || 'Jugador';
    setJugadorSeleccionado({
      idJugador: idJug,
      nombre: nomJug,
    });
    setIsCreateMode(true);
    try {
      const data = await obtenerInformesJugador(idJug);
      setInformesJugadorModal(data || []);
    } catch {
      setInformesJugadorModal([]);
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setIsCreateMode(false);
    setJugadorSeleccionado(null);
    setInformesJugadorModal([]);
  };

  const handleToggleVisibility = async (inf, nuevaVisibilidad) => {
    const res = await editarInforme(inf.idInforme, { ...inf, publica: nuevaVisibilidad });
    if (onUpdate && res) {
      onUpdate();
    }
  };

  const sortedInformes = useMemo(() => {
    const enriquecidos = (informes || []).map((inf) => ({
      ...inf,
      nota: calcularNotaInforme(inf) ?? -1,
    }));
    return ordenarLista(enriquecidos, sort);
  }, [informes, sort]);

  if (!informes) {
    return null;
  }

  return (
    <>
      {title && (
        <BoxTitleApp
          action={
            showCreateButton && (
              <Tooltip title="Crear informe" arrow placement="top">
                <Button
                  size="small"
                  onClick={() => setSelectorJugadorOpen(true)}
                  startIcon={<AddIcon sx={{ fontSize: '0.95rem !important' }} />}
                  sx={{
                    height: 26,
                    px: 1.25,
                    borderRadius: '13px',
                    backgroundColor: '#0f766e',
                    color: '#ffffff',
                    fontSize: '0.74rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0 1px 2px rgba(15, 118, 110, 0.2)',
                    '&:hover': {
                      backgroundColor: '#0d9488',
                      boxShadow: '0 2px 5px rgba(15, 118, 110, 0.3)',
                    },
                  }}
                >
                  Crear
                </Button>
              </Tooltip>
            )
          }
        >
          {title}
        </BoxTitleApp>
      )}

      <TableModuleApp
        noHeader={compact}
        noBorder
        columns={columns}
        sort={sort}
        setSort={setSort}
        data={sortedInformes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
        renderRow={(inf) => (
          <FilaInforme
            key={inf.idInforme}
            informe={inf}
            columns={columns}
            compact={compact}
            onSelectInforme={handleSelectInforme}
            onToggleVisibility={handleToggleVisibility}
          />
        )}
        hasPagination
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        rowsPerPageOptions={compact ? [5, 10, 20] : [10, 25, 50]}
        totalCount={sortedInformes.length}
      />

      <ModalSeleccionarJugador
        open={selectorJugadorOpen}
        onClose={() => setSelectorJugadorOpen(false)}
        onSelectJugador={handleSelectJugadorParaCrear}
        title="Crear informe - Seleccionar jugador"
        equiposClub={equiposClub}
      />

      {jugadorSeleccionado && (
        <ModalInformesJugador
          open={modalOpen}
          onClose={handleCloseModal}
          idJugador={jugadorSeleccionado.idJugador}
          nombreJugador={jugadorSeleccionado.nombre}
          informes={informesJugadorModal}
          initialCreateMode={isCreateMode}
          onUpdate={() => {
            if (onUpdate) {
              onUpdate();
            }
            handleCloseModal();
          }}
        />
      )}
    </>
  );
};

InformesUsuario.propTypes = {
  informes: PropTypes.array,
  onUpdate: PropTypes.func,
  compact: PropTypes.bool,
  showCreateButton: PropTypes.bool,
  equiposClub: PropTypes.array,
  title: PropTypes.node,
};

export default InformesUsuario;
